import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Calendar, MapPin, Clock, Loader2, ShoppingBag, FileText, Download } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownloadInvoice = async (order) => {
    setDownloadingId(order.id);
    try {
      const response = await axios.get(`${API}/orders/${order.id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const invoiceNum = order.invoice_number || order.id.slice(0, 8).toUpperCase();
      link.href = url;
      link.download = `facture-terreetmer-${invoiceNum}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({
        title: 'Facture téléchargée',
        description: `Facture n° ${invoiceNum} prête à l'impression.`,
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: "Impossible de télécharger la facture. Réessayez plus tard.",
        variant: 'destructive',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/dashboard');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [token]);

  const getStatusBadge = (status) => {
    const variants = {
      confirmed: { label: 'Confirmée', className: 'bg-green-100 text-green-700' },
      pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-700' },
      delivered: { label: 'Livrée', className: 'bg-blue-100 text-blue-700' },
      cancelled: { label: 'Annulée', className: 'bg-red-100 text-red-700' },
    };
    const variant = variants[status] || variants.pending;
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#E8945A]" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-12" data-testid="dashboard-page">
      {/* Header */}
      <section className="bg-[#F1F5F9] py-12 md:py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl tracking-tight font-medium text-[#2D3E50] mb-2">
            Bonjour, {user.first_name} !
          </h1>
          <p className="text-[#475569] text-lg">
            Gérez vos commandes et informations personnelles.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FFF5ED] rounded-xl flex items-center justify-center">
                <Package className="h-6 w-6 text-[#E8945A]" />
              </div>
              <div>
                <p className="text-sm text-[#475569]">Commandes totales</p>
                <p className="text-2xl font-semibold text-[#2D3E50]" data-testid="total-orders">
                  {orders.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FFF5ED] rounded-xl flex items-center justify-center">
                <Clock className="h-6 w-6 text-[#E8945A]" />
              </div>
              <div>
                <p className="text-sm text-[#475569]">En cours</p>
                <p className="text-2xl font-semibold text-[#2D3E50]">
                  {orders.filter(o => o.status === 'confirmed').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FFF5ED] rounded-xl flex items-center justify-center">
                <ShoppingBag className="h-6 w-6 text-[#E8945A]" />
              </div>
              <div>
                <p className="text-sm text-[#475569]">Total dépensé</p>
                <p className="text-2xl font-semibold text-[#2D3E50]">
                  {orders.reduce((sum, o) => sum + o.total_price, 0).toFixed(2)} €
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
          <div className="p-6 border-b border-[#E2E8F0] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="font-['Outfit'] text-xl font-medium text-[#2D3E50]">
                Historique des commandes
              </h2>
              <p className="text-xs text-[#64748B] mt-1">
                Téléchargez vos factures (PDF, conformes loi française) avec CGV annexées.
              </p>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FFF5ED] border border-[#E8945A]/20">
              <FileText className="h-4 w-4 text-[#E8945A]" />
              <span className="text-xs text-[#2D3E50] font-medium">
                Factures disponibles dès la confirmation
              </span>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-[#E8945A]" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16 px-6">
              <div className="w-16 h-16 bg-[#FFF5ED] rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8 text-[#E8945A]" />
              </div>
              <p className="text-[#475569] mb-4">Vous n'avez pas encore de commande</p>
              <Button
                className="bg-[#2D3E50] hover:bg-[#3D4F61] rounded-lg"
                onClick={() => navigate('/products')}
                data-testid="browse-products-dashboard"
              >
                Découvrir nos produits
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="orders-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Commande</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Séjour</TableHead>
                    <TableHead>Lieu</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Facture</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow
                      key={order.id}
                      data-testid={`order-row-${order.id}`}
                      className="cursor-pointer hover:bg-[#FAFAFA] transition-colors"
                      onClick={() => navigate(`/dashboard/orders/${order.id}`)}
                    >
                      <TableCell className="font-medium">
                        <div>
                          <div className="text-[#2D3E50]">
                            {order.invoice_number ? `N° ${order.invoice_number}` : `#${order.id.slice(0, 8).toUpperCase()}`}
                          </div>
                          <div className="text-xs text-[#64748B] font-normal">
                            #{order.id.slice(0, 8).toUpperCase()}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {format(new Date(order.created_at), 'dd MMM yyyy', { locale: fr })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-4 w-4 text-[#475569]" />
                          {format(new Date(order.arrival_date), 'dd/MM')} - {format(new Date(order.departure_date), 'dd/MM')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <MapPin className="h-4 w-4 text-[#475569]" />
                          {order.delivery_city}
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {order.total_price.toFixed(2)} €
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(order.status)}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 border-[#E8945A] text-[#E8945A] hover:bg-[#FFF5ED]"
                          onClick={() => handleDownloadInvoice(order)}
                          disabled={downloadingId === order.id}
                          data-testid={`download-invoice-${order.id}`}
                        >
                          {downloadingId === order.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                          <span className="hidden sm:inline">PDF</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
